import React, { useCallback, useEffect, useRef, useState } from "react";

import styled from "styled-components";

const Container = styled.div`
    position: relative;
    width: ${(props) => props.$width || "fit-content"};
    height: fit-content;
    display: inline-block;
`;

const LayerBox = styled.div`
    visibility: ${(props) => props.$visibility};
    top: ${(props) => props.$y}px;
    left: ${(props) => props.$x}px;
    position: fixed;
    display: -webkit-box;
    padding: 4px 6px;
    width: 300px;
    border-radius: 4px;
    letter-spacing: -0.25px;
    background-color: white;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
    opacity: 1;
    z-index: 100;
    white-space: normal;
`;

function Layer(props) {
    const { position = "bottomRight", width, children } = props;

    const containerRef = useRef(null);
    const layerRef = useRef(null);
    const hoverTimeoutRef = useRef(null);

    const [layerConfig, setLayerConfig] = useState({ visible: false, x: 0, y: 0 });

    const calculateLayerPosition = useCallback(() => {
        if (!containerRef.current || !layerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const layerHeight = layerRef.current.offsetHeight || 0;
        const layerWidth = layerRef.current.offsetWidth || 0;

        let x = rect.left;
        let y = rect.bottom;

        const space = 4;

        if (position === "top") {
            y = rect.top - layerHeight + space;
            x = rect.left + rect.width / 2 - layerWidth / 2;
        } else if (position === "left") {
            x = rect.left - layerWidth - space;
            y = rect.top + rect.height / 2 - layerHeight / 2;
        } else if (position === "right") {
            x = rect.right + space;
            y = rect.top + rect.height / 2 - layerHeight / 2;
        } else if (position === "bottom") {
            y = rect.bottom + space;
            x = rect.left + rect.width / 2 - layerWidth / 2;
        } else {
            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;

            if (x + layerWidth > windowWidth) {
                x = x - layerWidth + rect.width;
            }

            if (y + layerHeight > windowHeight) {
                y = rect.top - layerHeight - space;
            } else {
                y = rect.bottom + space;
            }
        }
        setLayerConfig({ visible: true, x, y });
    }, [position]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const handleMouseEnter = () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
                hoverTimeoutRef.current = null;
            }
            calculateLayerPosition();
        };

        const handleMouseLeave = () => {
            hoverTimeoutRef.current = setTimeout(() => {
                setLayerConfig({ visible: false, x: 0, y: 0 });
            }, 300);
        };

        container.addEventListener("mouseenter", handleMouseEnter);
        container.addEventListener("mouseleave", handleMouseLeave);

        return () => {
            container.removeEventListener("mouseenter", handleMouseEnter);
            container.removeEventListener("mouseleave", handleMouseLeave);
            clearTimeout(hoverTimeoutRef.current);
        };
    }, [calculateLayerPosition]);

    if (!props.targetContent) {
        return (
            <LayerBox className="layer" $x={props.x} $y={props.y} $visibility={props.show ? "visible" : "hidden"}>
                {children}
            </LayerBox>
        );
    }

    return (
        <Container $width={width} ref={containerRef}>
            <span
                style={{
                    fontWeight: "500",
                    borderRadius: "4px",
                    cursor: "default",
                    margin: "2px",
                    padding: "1px 2px",
                    color: "var(--color-SubBlack)",
                    backgroundColor: "var(--color-QuoteBox)",
                }}
            >
                {props.targetContent}
            </span>
            <LayerBox
                className="layer"
                $x={layerConfig.x}
                $y={layerConfig.y}
                $visibility={layerConfig.visible ? "visible" : "hidden"}
                ref={layerRef}
            >
                {children}
            </LayerBox>
        </Container>
    );
}

export default Layer;
